import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Card,
  List,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Button,
  Avatar,
  Space,
  Tag,
  Select,
  DatePicker,
  message,
  Tooltip,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import {
  getTeamComments,
  createTeamComment,
  getDashboardStatistics,
} from "../../api/accounting";
import ChartModal from "./ChartModal";
import {
  getCachedData,
  setCachedData,
  CacheTTL,
} from "../../context/AccountingDataCache";
import { SwapOutlined } from "@ant-design/icons";
import {
  FileTextOutlined,
  DollarOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  TableOutlined,
  MessageOutlined,
  SendOutlined,
  UserOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  FallOutlined,
  WalletOutlined,
  CreditCardOutlined,
  FileSyncOutlined,
  SettingOutlined,
  BankOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  AreaChartOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ReportsSidebar = ({ selectedBrands: selectedBrandsProp = [] }) => {
  const navigate = useNavigate();
  const reduxBrand = useSelector((state) => state.brand?.selectedBrand);

  // Use global filters hook for consistent brand/client selection
  const {
    selectedBrands: globalSelectedBrands,
    selectedClients,
  } = useGlobalFilters();

  // Currency display - for converting monetary values
  const { displayCurrency, displaySymbol, exchangeRate, isConverted, formatAmount, decimalPrecision } = useDisplayCurrency();

  // Helper to render monetary value with conversion indicator
  const renderMoneyValue = useCallback((usdValue) => {
    const value = parseFloat(usdValue) || 0;
    if (isConverted && exchangeRate) {
      const convertedValue = value * exchangeRate;
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>{convertedValue.toLocaleString('en-US', { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision })}</span>
          <Tooltip title={`Original: $${value.toLocaleString('en-US', { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision })} (Rate: 1 USD = ${exchangeRate} ${displayCurrency})`}>
            <SwapOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
          </Tooltip>
        </span>
      );
    }
    return value.toLocaleString('en-US', { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision });
  }, [isConverted, exchangeRate, displayCurrency, decimalPrecision]);

  // Use prop first, then global filters, then fallback to Redux, then localStorage
  const getBrandFromStorage = () => {
    try {
      const storedBrand = localStorage.getItem("selectedBrand");
      return storedBrand ? JSON.parse(storedBrand) : null;
    } catch {
      return null;
    }
  };

  // Handle both single brand (legacy) and multiple brands
  const selectedBrands =
    selectedBrandsProp && selectedBrandsProp.length > 0
      ? selectedBrandsProp
      : globalSelectedBrands && globalSelectedBrands.length > 0
      ? globalSelectedBrands
      : reduxBrand
      ? [reduxBrand]
      : getBrandFromStorage()
      ? [getBrandFromStorage()]
      : [];

  // Create stable brands key for caching
  const brandsKey = useMemo(() => {
    if (!selectedBrands || selectedBrands.length === 0) return "";
    return selectedBrands
      .map((b) => b.id)
      .sort()
      .join(",");
  }, [selectedBrands]);

  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState("info");
  const [comments, setComments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const statsFetchInFlightRef = useRef(false);
  const lastCompletedStatsFetchKeyRef = useRef(null);
  const fetchDashboardStatsRef = useRef(null);

  // Date filter for dashboard
  const [dateFilter, setDateFilter] = useState("this_month");
  const [customDateRange, setCustomDateRange] = useState(null);

  // Helper function to get date range based on filter
  const getDateRange = useCallback(() => {
    const today = dayjs();
    let start, end;

    switch (dateFilter) {
      case "this_month":
        start = today.startOf("month");
        end = today.endOf("month");
        break;
      case "last_month":
        start = today.subtract(1, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
        break;
      case "this_year":
        start = today.startOf("year");
        end = today; // YTD = year start to today
        break;
      case "custom":
        if (customDateRange && customDateRange.length === 2) {
          start = customDateRange[0];
          end = customDateRange[1];
        } else {
          // Fallback to this month if custom range not set
          start = today.startOf("month");
          end = today.endOf("month");
        }
        break;
      default:
        start = today.startOf("month");
        end = today.endOf("month");
    }

    return {
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
      displayStart: start,
      displayEnd: end,
    };
  }, [dateFilter, customDateRange]);

  // Generate cache key for dashboard stats
  const dashboardCacheKey = useMemo(() => {
    if (!brandsKey) return null;
    const { startDate, endDate } = getDateRange();
    const clientKey =
      selectedClients
        ?.map((c) => c?.id || c)
        .sort()
        .join(",") || "none";
    return `dashboard_stats_${brandsKey}_${clientKey}_${startDate}_${endDate}`;
  }, [brandsKey, selectedClients, getDateRange]);

  // Check global cache for initial data - instant restore on navigation
  const initialDashboardStats = useMemo(() => {
    if (!dashboardCacheKey) return null;
    return getCachedData(dashboardCacheKey);
  }, [dashboardCacheKey]);

  // Dashboard stats state - initialize from cache if available
  const [dashboardStats, setDashboardStats] = useState(
    () =>
      initialDashboardStats || {
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        pendingInvoices: 0,
        overduePayments: 0,
        totalTransactions: 0,
        activeAccounts: 0,
        monthlyGrowth: 0,
        totalCredits: 0,
        totalCashPayments: 0,
        totalOverpayments: 0,
        totalStatements: 0,
        unpaidBills: 0,
        creditNotesIssued: 0,
        cashPaymentsReceived: 0,
      }
  );

  // Chart modal state
  const [chartModalVisible, setChartModalVisible] = useState(false);
  const [activeChartType, setActiveChartType] = useState(null);
  const [activeChartTitle, setActiveChartTitle] = useState("");

  // Open chart modal
  const openChartModal = (chartType, title) => {
    setActiveChartType(chartType);
    setActiveChartTitle(title);
    setChartModalVisible(true);
  };

  // Fetch team comments
  const fetchComments = useCallback(async () => {
    try {
      const response = await getTeamComments({
        ordering: "-created_at",
      });

      // Handle both paginated (response.data.results) and direct array (response.data) formats
      const commentsData = response.data.results || response.data || [];
      setComments(commentsData);
    } catch (error) {
      console.error("Error fetching team comments:", error);
    }
  }, []);

  // Fetch comments when brand changes
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Fetch dashboard statistics
  const fetchDashboardStats = useCallback(async () => {
    if (!selectedBrands || selectedBrands.length === 0 || !dashboardCacheKey) {
      return;
    }

    const { startDate, endDate } = getDateRange();
    const brandIdList = selectedBrands
      .map((brand) => brand?.id)
      .filter((id) => id);

    if (brandIdList.length === 0) {
      return;
    }

    const sortedBrandIds = [...brandIdList].sort((a, b) => a - b);
    const clientIdList = selectedClients
      ? selectedClients.map((client) => client?.id || client).filter((id) => id)
      : [];
    const sortedClientIds = [...clientIdList].sort((a, b) => a - b);
    const fetchKey = dashboardCacheKey;

    // Check GLOBAL cache first - prevents refetch on navigation
    const cachedStats = getCachedData(dashboardCacheKey);
    if (cachedStats) {
      setDashboardStats(cachedStats);
      lastCompletedStatsFetchKeyRef.current = fetchKey;
      return;
    }

    if (
      statsFetchInFlightRef.current ||
      lastCompletedStatsFetchKeyRef.current === fetchKey
    ) {
      return;
    }

    statsFetchInFlightRef.current = true;
    setLoading(true);

    try {
      const stats = await getDashboardStatistics({
        brand_ids: sortedBrandIds,
        client_ids: sortedClientIds.length > 0 ? sortedClientIds : undefined,
        start_date: startDate,
        end_date: endDate,
      });

      setDashboardStats(stats);
      lastCompletedStatsFetchKeyRef.current = fetchKey;

      // Cache the results in GLOBAL cache - persists across page navigation
      setCachedData(dashboardCacheKey, stats, CacheTTL.MEDIUM);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      message.error("Could not load dashboard statistics.");
      setDashboardStats({
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        pendingInvoices: 0,
        overduePayments: 0,
        totalTransactions: 0,
        activeAccounts: 0,
        monthlyGrowth: 0,
        totalCredits: 0,
        totalCashPayments: 0,
        totalOverpayments: 0,
        totalStatements: 0,
        unpaidBills: 0,
        creditNotesIssued: 0,
        cashPaymentsReceived: 0,
      });
    } finally {
      statsFetchInFlightRef.current = false;
      setLoading(false);
    }
  }, [selectedBrands, selectedClients, getDateRange, dashboardCacheKey]);

  // Keep ref updated with latest fetchDashboardStats
  useEffect(() => {
    fetchDashboardStatsRef.current = fetchDashboardStats;
  }, [fetchDashboardStats]);

  useEffect(() => {
    // Skip if we already have cached data initialized for this key
    if (
      initialDashboardStats &&
      dashboardCacheKey &&
      lastCompletedStatsFetchKeyRef.current === dashboardCacheKey
    ) {
      return;
    }

    if (fetchDashboardStatsRef.current) {
      fetchDashboardStatsRef.current();
    }
  }, [
    selectedBrands,
    selectedClients,
    dateFilter,
    customDateRange,
    dashboardCacheKey,
    initialDashboardStats,
  ]);

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      message.warning("Please enter a comment");
      return;
    }

    setSubmitting(true);
    try {
      const commentData = {
        message: newComment.trim(),
        comment_type: commentType,
      };

      await createTeamComment(commentData);

      setNewComment("");
      setCommentType("info");
      message.success("Comment added successfully");

      await fetchComments();
    } catch (error) {
      message.error(
        `Failed to add comment: ${
          error.response?.data?.detail || error.message
        }`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReportClick = (reportType) => {
    switch (reportType) {
      case "Accounts Payable Report":
        navigate("/accounting/accounts-payable");
        break;
      case "Account Receivable Report":
        navigate("/accounting/accounts-receivable");
        break;
      case "Profit & Loss Statement":
        // Navigate to Financial Reports module with profit-loss tab
        navigate("/accounting/financial-reports?tab=profit-loss");
        break;
      case "Balance Sheet":
        // Navigate to Financial Reports module with balance-sheet tab
        navigate("/accounting/financial-reports?tab=balance-sheet");
        break;
      case "Payable Report":
        navigate("/accounting/accounts-payable");
        break;
      case "Allocation Report":
        navigate(
          "/accounting/transactions-allocations?tab=partner-transactions"
        );
        break;
      case "Vendor Report":
        navigate("/vendor-report");
        break;
      case "Vendor Aging Report":
        navigate("/vendor-aging-report");
        break;
      default:
        break;
    }
  };

  const getCommentTypeColor = (type) => {
    switch (type) {
      case "success":
        return "#52c41a";
      case "warning":
        return "#faad14";
      case "error":
        return "#f5222d";
      default:
        return "#1890ff";
    }
  };

  const reportTypes = [
    {
      title: "Profit & Loss Statement",
      icon: <FileTextOutlined style={{ fontSize: "20px", color: "#52c41a" }} />,
      description:
        "Professional financial performance and profit/loss reporting",
    },
    {
      title: "Balance Sheet",
      icon: <BankOutlined style={{ fontSize: "20px", color: "#1890ff" }} />,
      description:
        "Professional financial position analysis and balance sheet reporting",
    },
    {
      title: "Allocation Report",
      icon: <TableOutlined style={{ fontSize: "20px", color: "#13c2c2" }} />,
      description: "Payment and credit allocation details",
    },
    {
      title: "Vendor Report",
      icon: <UserOutlined style={{ fontSize: "20px", color: "#722ed1" }} />,
      description: "Comprehensive vendor performance and payment analysis",
    },
    {
      title: "Vendor Aging Report",
      icon: (
        <ClockCircleOutlined style={{ fontSize: "20px", color: "#faad14" }} />
      ),
      description: "View vendor invoice aging by payment due dates",
    },
  ];

  return (
    <div
      style={{ padding: "0", margin: "0", width: "100%", minHeight: "100vh" }}
    >
      <div style={{ padding: "16px", margin: "0" }}>
        <div
          style={{
            marginBottom: "24px",
          }}
        >
          <Title
            level={2}
            style={{
              margin: "0 0 16px 0",
              fontSize: "24px",
              fontWeight: "600",
            }}
          >
            Reports Dashboard
          </Title>
        </div>

        {/* Date Filter */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <strong>Period Filter:</strong>
                <Select
                  style={{ width: "100%" }}
                  value={dateFilter}
                  onChange={(value) => {
                    setDateFilter(value);
                    if (value !== "custom") {
                      setCustomDateRange(null);
                    }
                  }}
                >
                  <Option value="this_month">This Month</Option>
                  <Option value="last_month">Last Month</Option>
                  <Option value="this_year">This Year (YTD)</Option>
                  <Option value="custom">Custom Range</Option>
                </Select>
              </Space>
            </Col>
            {dateFilter === "custom" && (
              <Col xs={24} sm={12} md={8}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <strong>Custom Date Range:</strong>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={customDateRange}
                    onChange={setCustomDateRange}
                    format="YYYY-MM-DD"
                  />
                </Space>
              </Col>
            )}
            <Col xs={24} sm={12} md={8}>
              <div style={{ color: "#666", fontSize: 12 }}>
                <strong>Selected Period:</strong>
                <br />
                {(() => {
                  const { displayStart, displayEnd } = getDateRange();
                  return `${displayStart.format(
                    "MMM DD, YYYY"
                  )} - ${displayEnd.format("MMM DD, YYYY")}`;
                })()}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Dashboard Widgets - Clickable for Charts */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={12} lg={6}>
            <Tooltip title="Click to view Revenue Chart">
              <Card
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  borderLeft: "4px solid #52c41a",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                hoverable
                loading={loading}
                onClick={() => openChartModal("revenue", "Revenue Analysis")}
              >
                <Statistic
                  title={
                    <Space>
                      <span>Total Revenue</span>
                      <AreaChartOutlined
                        style={{ color: "#52c41a", fontSize: 14 }}
                      />
                    </Space>
                  }
                  value={isConverted ? (dashboardStats.totalRevenue * exchangeRate) : dashboardStats.totalRevenue}
                  precision={decimalPrecision}
                  valueStyle={{ color: "#52c41a" }}
                  prefix={isConverted ? <><RiseOutlined /> <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} /></> : <RiseOutlined />}
                  suffix={displayCurrency}
                />
              </Card>
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Tooltip title="Click to view Expenses Chart">
              <Card
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  borderLeft: "4px solid #f5222d",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                hoverable
                loading={loading}
                onClick={() => openChartModal("expenses", "Expenses Analysis")}
              >
                <Statistic
                  title={
                    <Space>
                      <span>Total Expenses</span>
                      <AreaChartOutlined
                        style={{ color: "#f5222d", fontSize: 14 }}
                      />
                    </Space>
                  }
                  value={isConverted ? (dashboardStats.totalExpenses * exchangeRate) : dashboardStats.totalExpenses}
                  precision={decimalPrecision}
                  valueStyle={{ color: "#f5222d" }}
                  prefix={isConverted ? <><FallOutlined /> <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} /></> : <FallOutlined />}
                  suffix={displayCurrency}
                />
              </Card>
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Tooltip title="Click to view Profit & Loss Chart">
              <Card
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  borderLeft: "4px solid #1890ff",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                hoverable
                loading={loading}
                onClick={() =>
                  openChartModal("profit", "Profit & Loss Analysis")
                }
              >
                <Statistic
                  title={
                    <Space>
                      <span>Net Profit</span>
                      <BarChartOutlined
                        style={{ color: "#1890ff", fontSize: 14 }}
                      />
                    </Space>
                  }
                  value={isConverted ? (dashboardStats.netProfit * exchangeRate) : dashboardStats.netProfit}
                  precision={decimalPrecision}
                  valueStyle={{ color: "#1890ff" }}
                  prefix={isConverted ? <><WalletOutlined /> <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} /></> : <WalletOutlined />}
                  suffix={displayCurrency}
                />
              </Card>
            </Tooltip>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Tooltip title="Click to view Growth Trend">
              <Card
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  borderLeft: "4px solid #faad14",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                hoverable
                loading={loading}
                onClick={() => openChartModal("growth", "Monthly Growth Trend")}
              >
                <Statistic
                  title={
                    <Space>
                      <span>Monthly Growth</span>
                      <LineChartOutlined
                        style={{ color: "#faad14", fontSize: 14 }}
                      />
                    </Space>
                  }
                  value={dashboardStats.monthlyGrowth}
                  precision={1}
                  valueStyle={{ color: "#faad14" }}
                  prefix={<BarChartOutlined />}
                  suffix="%"
                />
              </Card>
            </Tooltip>
          </Col>
        </Row>

        {/* Secondary Stats Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={8} lg={4}>
            <Card
              size="small"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
              loading={loading}
            >
              <Statistic
                title="Pending Invoices"
                value={dashboardStats.pendingInvoices}
                valueStyle={{ color: "#1890ff", fontSize: "20px" }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} lg={4}>
            <Card
              size="small"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
              loading={loading}
            >
              <Statistic
                title="Overdue Payments"
                value={dashboardStats.overduePayments}
                valueStyle={{ color: "#f5222d", fontSize: "20px" }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} lg={4}>
            <Card
              size="small"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
              loading={loading}
            >
              <Statistic
                title="Total Transactions"
                value={dashboardStats.totalTransactions}
                valueStyle={{ color: "#52c41a", fontSize: "20px" }}
                prefix={<CreditCardOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} lg={4}>
            <Card
              size="small"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
              loading={loading}
            >
              <Statistic
                title="Active Accounts"
                value={dashboardStats.activeAccounts}
                valueStyle={{ color: "#722ed1", fontSize: "20px" }}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} lg={4}>
            <Card
              size="small"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
              loading={loading}
            >
              <Statistic
                title="Credit Notes"
                value={dashboardStats.creditNotesIssued}
                valueStyle={{ color: "#13c2c2", fontSize: "20px" }}
                prefix={<FileSyncOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} lg={4}>
            <Card
              size="small"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
              loading={loading}
            >
              <Statistic
                title="Cash Payments"
                value={dashboardStats.cashPaymentsReceived}
                valueStyle={{ color: "#fa8c16", fontSize: "20px" }}
                prefix={<SettingOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Additional Stats Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={8} lg={6}>
            <Tooltip title="Click to view Vendor Distribution">
              <Card
                size="small"
                style={{
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                hoverable
                loading={loading}
                onClick={() =>
                  openChartModal("vendors", "Vendor Spend Distribution")
                }
              >
                <Statistic
                  title={
                    <Space size={4}>
                      <span>Unpaid Bills</span>
                      <PieChartOutlined
                        style={{ fontSize: 12, color: "#ff4d4f" }}
                      />
                    </Space>
                  }
                  value={dashboardStats.unpaidBills}
                  valueStyle={{ color: "#ff4d4f", fontSize: "20px" }}
                  prefix={<FileTextOutlined />}
                />
              </Card>
            </Tooltip>
          </Col>
          <Col xs={24} sm={8} lg={6}>
            <Card
              size="small"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
              loading={loading}
            >
              <Statistic
                title="Bank Statements"
                value={dashboardStats.totalStatements}
                valueStyle={{ color: "#1890ff", fontSize: "20px" }}
                prefix={<FileSyncOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} lg={6}>
            <Card
              size="small"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
              loading={loading}
            >
              <Statistic
                title="Overpayments"
                value={isConverted ? (dashboardStats.totalOverpayments * exchangeRate) : dashboardStats.totalOverpayments}
                precision={0}
                valueStyle={{ color: "#52c41a", fontSize: "18px" }}
                prefix={isConverted ? <><DollarOutlined /> <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} /></> : <DollarOutlined />}
                suffix={displayCurrency}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} lg={6}>
            <Card
              size="small"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
              loading={loading}
            >
              <Statistic
                title="Cash Amount"
                value={isConverted ? (dashboardStats.totalCashPayments * exchangeRate) : dashboardStats.totalCashPayments}
                precision={0}
                valueStyle={{ color: "#722ed1", fontSize: "18px" }}
                prefix={isConverted ? <><WalletOutlined /> <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} /></> : <WalletOutlined />}
                suffix={displayCurrency}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {/* Reports List */}
          <Col xs={24} lg={16}>
            <Card
              title="Available Reports"
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                height: "100%",
              }}
            >
              <List
                dataSource={reportTypes}
                renderItem={(item) => (
                  <List.Item
                    style={{
                      padding: "16px 0",
                      borderBottom: "1px solid #f0f0f0",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f5f5f5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                    onClick={() => handleReportClick(item.title)}
                  >
                    <List.Item.Meta
                      avatar={item.icon}
                      title={
                        <span
                          style={{
                            fontSize: "16px",
                            fontWeight: "500",
                            color: "#262626",
                          }}
                        >
                          {item.title}
                        </span>
                      }
                      description={
                        <span
                          style={{
                            fontSize: "14px",
                            color: "#8c8c8c",
                            marginTop: "4px",
                          }}
                        >
                          {item.description}
                        </span>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Comments Widget */}
          <Col xs={24} lg={8}>
            <Card
              title={
                <Space>
                  <MessageOutlined />
                  Team Comments
                </Space>
              }
              style={{
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                height: "100%",
              }}
              bodyStyle={{ padding: "16px" }}
            >
              <div style={{ marginBottom: "16px" }}>
                <Select
                  value={commentType}
                  onChange={setCommentType}
                  style={{ width: "100%", marginBottom: "8px" }}
                  size="small"
                >
                  <Option value="info">
                    <Space>
                      <InfoCircleOutlined style={{ color: "#1890ff" }} />
                      Info
                    </Space>
                  </Option>
                  <Option value="success">
                    <Space>
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      Success
                    </Space>
                  </Option>
                  <Option value="warning">
                    <Space>
                      <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                      Warning
                    </Space>
                  </Option>
                  <Option value="error">
                    <Space>
                      <CloseCircleOutlined style={{ color: "#f5222d" }} />
                      Error
                    </Space>
                  </Option>
                </Select>
                <TextArea
                  rows={3}
                  placeholder="Add a team comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  style={{ marginBottom: "8px" }}
                  onPressEnter={(e) => {
                    if (e.shiftKey) return; // Allow shift+enter for new line
                    e.preventDefault();
                    handleAddComment();
                  }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleAddComment}
                  loading={submitting}
                  disabled={!newComment.trim()}
                  style={{ width: "100%" }}
                >
                  Add Comment
                </Button>
              </div>

              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    style={{
                      marginBottom: "16px",
                      padding: "12px",
                      backgroundColor: "#fafafa",
                      borderRadius: "8px",
                      borderLeft: `4px solid ${getCommentTypeColor(
                        comment.comment_type
                      )}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Avatar
                        size="small"
                        style={{
                          backgroundColor: getCommentTypeColor(
                            comment.comment_type
                          ),
                          fontSize: "12px",
                        }}
                        icon={<UserOutlined />}
                      >
                        {comment.user_initials || "U"}
                      </Avatar>
                      <div style={{ marginLeft: "8px", flex: 1 }}>
                        <div style={{ fontWeight: "500", fontSize: "14px" }}>
                          {comment.user_display_name}
                        </div>
                        <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                          {comment.time_ago}
                        </div>
                      </div>
                      <Tag
                        color={getCommentTypeColor(comment.comment_type)}
                        size="small"
                      >
                        {comment.comment_type}
                      </Tag>
                    </div>
                    <div style={{ fontSize: "14px", color: "#262626" }}>
                      {comment.message}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Chart Modal */}
      <ChartModal
        visible={chartModalVisible}
        onClose={() => setChartModalVisible(false)}
        chartType={activeChartType}
        title={activeChartTitle}
        brandIds={selectedBrands.map((b) => b?.id).filter(Boolean)}
        clientIds={
          selectedClients
            ? selectedClients.map((c) => c?.id || c).filter(Boolean)
            : []
        }
        dateRange={getDateRange()}
      />
    </div>
  );
};

export default ReportsSidebar;
